import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
    Modal,
    Form,
    Input,
    InputNumber,
    Button,
    Upload,
    Image,
    Select,
    Tag,
    notification,
    Spin,
    Popconfirm,
    Tabs,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
    getService,
    updateService,
    deleteService,
    getResourcesCategoriesWithResources,
} from "../../../service";
import { PriceInput } from "../../atoms/PriceInput";
import { MediaEditor } from "../media-editor/MediaEditor";
import { colors } from "../../../styles/theme";
import { ServiceAvailabilityTab } from "./ServiceAvailabilityTab";
import { useBranchContext } from "../../../contexts/branch-context/useBranchContext";
import styled from "styled-components";
import Cookies from "js-cookie";

const accessToken = Cookies.get("accessToken");
const PATH = process.env.REACT_APP_API_URL || "http://localhost:4000";

const StyledUpload = styled(Upload)`
  .ant-upload-list-item,
  .ant-upload {
    width: 100% !important;
    height: 200px !important;
  }
`;

interface ResourceCategory {
    id: number;
    name: string;
    resources?: { id: number; name: string; resourceCategoryId: number }[];
}

interface EditServiceModalProps {
    open: boolean;
    onCancel: () => void;
    serviceId: number | null;
    categoryId: number;
    onSuccess: () => void;
}

const validateImageFile = (file: any, t: TFunction): Promise<boolean> => {
    // Check file type
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
        notification.error({ message: t("editServiceModal.onlyImagesAllowed") });
        return Promise.resolve(false);
    }

    // Check file format
    const validFormats = ["image/jpeg", "image/png", "image/webp"];
    if (!validFormats.includes(file.type)) {
        notification.error({
            message: t("editServiceModal.invalidFileFormat"),
            description: t("editServiceModal.allowedFormats"),
        });
        return Promise.resolve(false);
    }

    // Check file size (max 10MB before editing)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
        notification.error({
            message: t("editServiceModal.fileTooLarge"),
            description: t("editServiceModal.maxSize"),
        });
        return Promise.resolve(false);
    }

    return new Promise((resolve) => {
        const img = new window.Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(img.src);
            resolve(true);
        };
        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            resolve(false);
        };
    });
};

export const EditServiceModal: React.FC<EditServiceModalProps> = ({
    open,
    onCancel,
    serviceId,
    categoryId,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [price, setPrice] = useState(0);
    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
    const [selectedResourceCategories, setSelectedResourceCategories] = useState<number[]>([]);
    const [selectedResources, setSelectedResources] = useState<number[]>([]);
    const [editorVisible, setEditorVisible] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const { availableBranches, currentBranch } = useBranchContext();
    const [branchesConfig, setBranchesConfig] = useState<Record<number, any>>({});

    const { data: service, isLoading } = useQuery(
        ["/service", serviceId],
        () => getService({ id: serviceId!.toString() }),
        {
            enabled: !!serviceId && open,
            onSuccess(data) {
                form.setFieldsValue({
                    name: data.name,
                    description: data.description,
                    duration: data.duration,
                });
                setPrice(data.price || 0);
                setImageUrl(data.imageUrlS3);

                if (data.resourceCategoriesNeeded) {
                    setSelectedResourceCategories(
                        data.resourceCategoriesNeeded.map((rc: any) => rc.resourceCategoryId)
                    );
                }

                if (data.resourcesProviding) {
                    setSelectedResources(
                        data.resourcesProviding.map((r: any) => r.resourceId)
                    );
                }
            },
        }
    );

    useEffect(() => {
        if (service && availableBranches.length > 0) {
            const defaultSchedules = {
                friday: [{ from: "00:00", till: "23:45" }],
                monday: [{ from: "00:00", till: "23:45" }],
                sunday: [{ from: "00:00", till: "23:45" }],
                tuesday: [{ from: "00:00", till: "23:45" }],
                saturday: [{ from: "00:00", till: "23:45" }],
                thursday: [{ from: "00:00", till: "23:45" }],
                wednesday: [{ from: "00:00", till: "23:45" }],
            };
            const config: Record<number, any> = {};
            availableBranches.forEach((branch: any) => {
                const existingBranchData = service.branches?.find((b: any) => b.id === branch.id);
                config[branch.id] = existingBranchData ? {
                    isVisible: existingBranchData.isVisible,
                    isAvailable: existingBranchData.isAvailable,
                    availabilitySchedules: existingBranchData.availabilitySchedules || defaultSchedules
                } : {
                    isVisible: true,
                    isAvailable: true,
                    availabilitySchedules: defaultSchedules
                };
            });
            setBranchesConfig(config);
        }
    }, [service, availableBranches]);

    const { data: resourceCategories } = useQuery<ResourceCategory[]>(
        ["/resource-categories-with-resources"],
        () => getResourcesCategoriesWithResources(),
        {
            enabled: open,
        }
    );

    const { mutateAsync: updateMutation, isLoading: updateIsLoading } =
        useMutation(updateService, {
            onSuccess: () => {
                notification.success({
                    message: t("editServiceModal.serviceUpdated"),
                    style: { background: colors.successBackground },
                });
                onSuccess();
            },
            onError: () => {
                notification.error({
                    message: t("editServiceModal.serviceUpdateError"),
                    style: { background: colors.errorBackground },
                });
            },
        });

    const { mutateAsync: deleteMutation, isLoading: deleteIsLoading } =
        useMutation(deleteService, {
            onSuccess: () => {
                notification.success({
                    message: t("editServiceModal.serviceDeleted"),
                    style: { background: colors.successBackground },
                });
                onSuccess();
            },
            onError: () => {
                notification.error({
                    message: t("editServiceModal.serviceDeleteError"),
                    style: { background: colors.errorBackground },
                });
            },
        });

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (serviceId && service) {
                const branchesPayload = Object.entries(branchesConfig).map(([branchId, config]) => ({
                    id: Number(branchId),
                    isVisible: config.isVisible,
                    isAvailable: config.isAvailable,
                    availabilitySchedules: config.availabilitySchedules
                }));

                await updateMutation({
                    id: serviceId,
                    body: {
                        ...values,
                        price,
                        serviceCategoryId: categoryId,
                        resourceCategoriesNeeded: selectedResourceCategories,
                        resourcesProviding: selectedResources,
                        branches: branchesPayload,
                    },
                });
            }
        } catch (error) {
            // Form validation failed
        }
    };

    const handleDelete = async () => {
        if (serviceId) {
            await deleteMutation(serviceId);
        }
    };

    const handleRemoveResourceCategory = (id: number) => {
        setSelectedResourceCategories((prev) => prev.filter((catId) => catId !== id));
    };

    const handleRemoveResource = (id: number) => {
        setSelectedResources((prev) => prev.filter((resId) => resId !== id));
    };

    const handleBeforeUpload = async (file: File) => {
        const isValid = await validateImageFile(file, t);
        if (isValid) {
            setSelectedFile(file);
            setEditorVisible(true);
        }
        return false;
    };

    const handleEditorConfirm = async (croppedBlob: Blob) => {
        if (!serviceId) return;

        const formData = new FormData();
        formData.append("file", croppedBlob, "cropped-image.jpg");

        try {
            const response = await fetch(
                `${PATH}/v1/services_api/service/${serviceId}/image`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: formData,
                }
            );

            if (response.ok) {
                notification.success({ message: t("editServiceModal.imageUploaded") });
                queryClient.invalidateQueries(["/service", serviceId]);
            } else {
                throw new Error("Upload failed");
            }
        } catch (error) {
            notification.error({ message: t("editServiceModal.imageUploadError") });
        } finally {
            setEditorVisible(false);
            setSelectedFile(null);
        }
    };

    useEffect(() => {
        if (imageUrl) {
            setPreviewImage(`${imageUrl}?id=${new Date().getTime()}`);
            setFileList([
                {
                    uid: "-1",
                    name: "image.png",
                    status: "done",
                    url: `${imageUrl}?id=${new Date().getTime()}`,
                },
            ]);
        } else {
            setPreviewImage("");
            setFileList([]);
        }
    }, [imageUrl]);

    // Get available resource categories (not already selected)
    const availableResourceCategories = resourceCategories?.filter(
        rc => !selectedResourceCategories.includes(rc.id)
    ) || [];

    // Get all resources from selected categories
    const availableResources = resourceCategories
        ?.flatMap(rc => rc.resources || [])
        .filter(r => !selectedResources.includes(r.id)) || [];

    if (!open) return null;

    return (
        <>
            <Modal
                title={t("editServiceModal.title")}
                open={open}
                onCancel={onCancel}
                width={800}
                footer={[
                    <Popconfirm
                        title={t("editServiceModal.deleteConfirm")}
                        onConfirm={handleDelete}
                        okText={t("editServiceModal.yes")}
                        cancelText={t("editServiceModal.no")}
                        key="delete"
                    >
                        <Button danger loading={deleteIsLoading}>
                            {t("editServiceModal.deleteService")}
                        </Button>
                    </Popconfirm>,
                    <Button key="cancel" onClick={onCancel}>
                        {t("editServiceModal.cancel")}
                    </Button>,
                    <Button key="save" type="primary" loading={updateIsLoading} onClick={handleSave}>
                        {t("editServiceModal.saveChanges")}
                    </Button>,
                ]}
            >
                {isLoading ? (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Tabs defaultActiveKey="basic" items={[
                        {
                            key: "basic",
                            label: t("editServiceModal.tabBasic"),
                            children: (
                                <div style={{ padding: "16px 0" }}>
                                    <Form form={form} layout="vertical">
                                        <Form.Item
                                            label={t("editServiceModal.nameLabel")}
                                            name="name"
                                            rules={[{ required: true, message: t("editServiceModal.nameRequired") }]}
                                        >
                                            <Input placeholder={t("editServiceModal.namePlaceholder")} />
                                        </Form.Item>

                                        <Form.Item label={t("editServiceModal.descriptionLabel")} name="description">
                                            <Input.TextArea
                                                rows={4}
                                                placeholder={t("editServiceModal.descriptionPlaceholder")}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label={t("editServiceModal.durationLabel")}
                                            name="duration"
                                            rules={[{ required: true, message: t("editServiceModal.durationRequired") }]}
                                        >
                                            <InputNumber min={1} style={{ width: "100%" }} placeholder="60" />
                                        </Form.Item>

                                        <Form.Item label={t("editServiceModal.priceLabel")}>
                                            <PriceInput
                                                value={price}
                                                onChange={setPrice}
                                                editMode={true}
                                                width="100%"
                                                currency={currentBranch?.currency}
                                            />
                                        </Form.Item>
                                    </Form>
                                </div>
                            ),
                        },
                        {
                            key: "resources",
                            label: t("editServiceModal.tabResources"),
                            children: (
                                <div style={{ padding: "16px 0" }}>
                                    <div style={{ marginBottom: 24 }}>
                                        <h4 style={{ marginBottom: 8 }}>{t("editServiceModal.requiredResourceCategories")}</h4>
                                        <p style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 12 }}>
                                            {t("editServiceModal.requiredResourceCategoriesHint")}
                                        </p>
                                        <div style={{ marginBottom: 12 }}>
                                            {selectedResourceCategories.map((catId) => {
                                                const cat = resourceCategories?.find((rc) => rc.id === catId);
                                                return cat ? (
                                                    <Tag
                                                        key={catId}
                                                        closable
                                                        onClose={() => handleRemoveResourceCategory(catId)}
                                                        color="blue"
                                                        style={{ marginBottom: 8 }}
                                                    >
                                                        {cat.name}
                                                    </Tag>
                                                ) : null;
                                            })}
                                        </div>
                                        <Select
                                            style={{ width: "100%" }}
                                            placeholder={t("editServiceModal.addResourceCategory")}
                                            value={null}
                                            onChange={(value) => {
                                                if (value) setSelectedResourceCategories((prev) => [...prev, value]);
                                            }}
                                            options={availableResourceCategories.map((rc) => ({
                                                label: rc.name,
                                                value: rc.id,
                                            }))}
                                        />
                                    </div>

                                    <div>
                                        <h4 style={{ marginBottom: 8 }}>{t("editServiceModal.providingResources")}</h4>
                                        <p style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 12 }}>
                                            {t("editServiceModal.providingResourcesHint")}
                                        </p>
                                        <div style={{ marginBottom: 12 }}>
                                            {selectedResources.map((resId) => {
                                                const res = resourceCategories
                                                    ?.flatMap((rc) => rc.resources || [])
                                                    .find((r) => r.id === resId);
                                                return res ? (
                                                    <Tag
                                                        key={resId}
                                                        closable
                                                        onClose={() => handleRemoveResource(resId)}
                                                        color="green"
                                                        style={{ marginBottom: 8 }}
                                                    >
                                                        {res.name}
                                                    </Tag>
                                                ) : null;
                                            })}
                                        </div>
                                        <Select
                                            style={{ width: "100%" }}
                                            placeholder={t("editServiceModal.addResource")}
                                            value={null}
                                            onChange={(value) => {
                                                if (value) setSelectedResources((prev) => [...prev, value]);
                                            }}
                                            options={availableResources.map((r) => ({
                                                label: r.name,
                                                value: r.id,
                                            }))}
                                        />
                                    </div>
                                </div>
                            ),
                        },
                        {
                            key: "image",
                            label: t("editServiceModal.tabImage"),
                            children: (
                                <div style={{ padding: "16px 0", display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <StyledUpload
                                        beforeUpload={handleBeforeUpload}
                                        listType="picture-card"
                                        fileList={fileList}
                                        onPreview={() => setPreviewOpen(true)}
                                        showUploadList={{
                                            showPreviewIcon: true,
                                            showRemoveIcon: false,
                                        }}
                                    >
                                        {fileList.length >= 1 ? null : (
                                            <div style={{ marginTop: 8 }}>
                                                <PlusOutlined />
                                                <div style={{ marginTop: 8 }}>{t("editServiceModal.upload")}</div>
                                            </div>
                                        )}
                                    </StyledUpload>
                                    {previewImage && (
                                        <Image
                                            wrapperStyle={{ display: "none" }}
                                            preview={{
                                                visible: previewOpen,
                                                onVisibleChange: (visible) => setPreviewOpen(visible),
                                                afterOpenChange: (visible) => !visible && setPreviewImage(""),
                                            }}
                                            src={`${previewImage}?id=${new Date().getTime()}`}
                                        />
                                    )}
                                </div>
                            ),
                        },
                        {
                            key: "availability",
                            label: t("editServiceModal.tabAvailability"),
                            children: (
                                <div style={{ padding: "16px 0" }}>
                                    <ServiceAvailabilityTab
                                        branchesConfig={branchesConfig}
                                        setBranchesConfig={setBranchesConfig}
                                    />
                                </div>
                            ),
                        },
                    ]} />
                )}
            </Modal>

            {selectedFile && (
                <MediaEditor
                    visible={editorVisible}
                    file={selectedFile}
                    onConfirm={handleEditorConfirm}
                    onCancel={() => {
                        setEditorVisible(false);
                        setSelectedFile(null);
                    }}
                />
            )}
        </>
    );
};
